interface EventTrace {
  timestamp: number;
  stackTrace: string;
}

interface RouteEventTracker {
  route: string;
  events: Set<string>;
  eventTraces: Map<string, EventTrace>;
  timestamp: number;
}

class EventTracker {
  private currentRouteTracker: RouteEventTracker | null = null;
  private customUserProperties: Record<string, string> = {};

  public setCustomUserProperties(params: Record<string, string>): void {
    this.customUserProperties = {
      ...this.customUserProperties,
      ...params,
    };
  }

  public getCustomUserProperties(): Record<string, string> {
    return this.customUserProperties;
  }

  public setCurrentRoute(route: string): void {
    this.currentRouteTracker = {
      route,
      events: new Set(),
      eventTraces: new Map(),
      timestamp: Date.now(),
    };
  }

  public trackEvent(
    eventName: string,
    route: string,
    source?: string,
  ): {
    isDuplicate: boolean;
    previousTimestamp?: number;
    originalTrace?: string;
    currentTrace?: string;
  } {
    if (
      this.currentRouteTracker === null ||
      this.currentRouteTracker.route !== route
    ) {
      this.setCurrentRoute(route);
    }

    if (this.currentRouteTracker === null) {
      return { isDuplicate: false };
    }

    const eventKey = `${eventName}:${route}:${source ?? ''}`;
    const isDuplicate = this.currentRouteTracker.events.has(eventKey);
    const currentTrace = this.captureStackTrace();

    if (isDuplicate) {
      const originalTrace = this.currentRouteTracker.eventTraces.get(eventKey);
      return {
        isDuplicate: true,
        previousTimestamp:
          originalTrace?.timestamp ?? this.currentRouteTracker.timestamp,
        originalTrace: originalTrace?.stackTrace,
        currentTrace,
      };
    }

    this.currentRouteTracker.events.add(eventKey);
    this.currentRouteTracker.eventTraces.set(eventKey, {
      timestamp: Date.now(),
      stackTrace: currentTrace,
    });

    return { isDuplicate: false };
  }

  private captureStackTrace(): string {
    const error = new Error();
    const stack = error.stack ?? '';

    const lines = stack.split('\n');
    const filteredLines = lines.filter((line, index) => {
      if (index === 0) return false;
      if (line.includes('event-tracker.ts')) return false;
      if (line.includes('log-hubx-event.ts')) return false;
      return true;
    });

    return filteredLines[0] ?? 'Stack trace not available';
  }

  public clearRoute(): void {
    this.currentRouteTracker = null;
  }

  public getCurrentRoute(): string | null {
    return this.currentRouteTracker !== null
      ? this.currentRouteTracker.route
      : null;
  }

  public getTrackedEvents(): string[] {
    return this.currentRouteTracker !== null
      ? Array.from(this.currentRouteTracker.events)
      : [];
  }
}

export const eventTracker = new EventTracker();
